import React from 'react';
import {
  Button, Empty, List, Tabs, Typography,
} from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router-dom';
import { UserReport } from '@services/userreport/types';
import CancelReportExportButton from './CancelReportExportButton';
import ReportExportProgress from './ReportExportProgress';
import ReportExportStatusBadge from './ReportExportStatusBadge';
import { reportFileName } from './utils';

dayjs.extend(relativeTime);

type Props = { reports: UserReport[]; loading: boolean };

const MAX_VISIBLE_REPORTS = 5;

const reportAccentColor = (report: UserReport) => {
  const emailSent = Boolean(report.emailSentAt) || report.progressMessage === 'email_sent';

  if (report.state === 'failed') return '#ef4444';
  if (report.state === 'processing') return '#3b82f6';
  if (report.state === 'queued') return '#8b5cf6';
  if (report.state === 'cancellation_requested') return '#f59e0b';
  if (report.state === 'canceled') return '#64748b';
  if (report.state === 'ready' || emailSent) return '#10b981';
  return '#9ca3af';
};

const ReportExportPopover = ({ reports, loading }: Props) => {
  const { t } = useTranslation();
  const { TabPane } = Tabs;
  const navigate = useNavigate();
  const visibleReports = reports.slice(0, MAX_VISIBLE_REPORTS);

  const exportsContent = !loading && reports.length === 0 ? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('exportReports.empty')} />
  ) : (
    <>
      <List
        loading={loading}
        dataSource={visibleReports}
        renderItem={(report) => (
          <List.Item
            className="pl-4"
            style={{ boxShadow: `inset 4px 0 0 ${reportAccentColor(report)}` }}
            actions={[
              <CancelReportExportButton
                key="cancel"
                reportId={report.id}
                canCancel={report.canCancel}
              />,
            ]}
          >
            <List.Item.Meta
              avatar={(
                <FileExcelOutlined
                  className="text-2xl"
                  style={{ color: reportAccentColor(report) }}
                />
              )}
              title={(
                <span className="block truncate capitalize">{reportFileName(report)}</span>
              )}
              description={(
                <div>
                  <div><ReportExportStatusBadge report={report} /></div>
                  <div>{dayjs(report.createdAt).fromNow()}</div>
                  <ReportExportProgress report={report} />
                  {report.state === 'failed' && report.errorMessage && (
                    <Typography.Text type="danger">{report.errorMessage}</Typography.Text>
                  )}
                </div>
              )}
            />
          </List.Item>
        )}
      />
      <div className="border-t border-gray-200 text-center">
        <Button
          type="link"
          onClick={() => navigate('/downloads')}
        >
          {t('exportReports.showMore')}
        </Button>
      </div>
    </>
  );

  return (
    <div className="w-96 max-w-full">
      <Tabs defaultActiveKey="exports">
        <TabPane tab={t('exportReports.generalTab')} key="general">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('exportReports.generalEmpty')}
          />
        </TabPane>
        <TabPane tab={t('exportReports.title')} key="exports">
          {exportsContent}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ReportExportPopover;
